import { useQuery, useQueryClient, useMutation } from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
  CountOrganizationDto,
  CreateOrganizationDto,
  OrganizationDto,
  OrganizationPaginator,
  OrganizationWithOptionalRelationsPaginator,
  QueryOrganizationDto,
  SearchOrganizationDto,
  UpdateOrganizationDto,
  UpdateOrgBillingTypeDto,
  UpdateOrgSubscriptionTierDto,
} from "../models/organization.dto";
import { ChangeOrganizationSubscriptionDto } from "../models/subscription.dto";
import ENDPOINTS from "./utils/endpoints";
import { CompanyPaginator } from "../models/company-account.dto";
import useCompanyStore from "../state-management/useCompanyStore";
import { APP_CONFIG } from "../helpers/appHelpers";

export function useGetOrganization(options?: QueryOrganizationDto) {
  const url = ENDPOINTS.ORGANIZATION.GET;
  const request = (options: QueryOrganizationDto) =>
    API.OrganizationAPI.getOrganization(options);

  const { data, isLoading, error, refetch } = useQuery<
    OrganizationWithOptionalRelationsPaginator,
    Error
  >(
    [url, options],
    ({ queryKey, pageParam }) =>
      request(Object.assign({}, queryKey[1], pageParam)),
    { ...API.Handlers.pageParamOption },
  );

  return { data, isLoading, error, refetch };
}

export function useGetOrganizationBySearch() {
  return useMutation(
    (options?: SearchOrganizationDto) =>
      API.OrganizationAPI.getOrganizationSearch(options),
    {
      onSuccess: () => {
        //toast.success('Record created successfully');
      },
      onError: API.Handlers.onErrorHandler,
    },
  );
}

export function useGetCompaniesByOrganizationId(
  organizationId: string,
  useSaved: boolean = true,
) {
  const { savedCompanies } = useCompanyStore();
  const url = ENDPOINTS.PROCESSING_COMPANY.COMPANY.GET;

  const storedCompanies: Promise<CompanyPaginator> = new Promise(function (
    resolve,
    reject,
  ) {
    return resolve({
      data: savedCompanies,
      page: 0,
      pageSize: 0,
      totalCount: 0,
      status: 200,
      requestId: "",
      errors: null,
      message: "",
    });
  });

  const request =
    savedCompanies.length && useSaved
      ? storedCompanies
      : API.ProcessingCompanyAPI.getCompany({
          organizationId,
          limit: APP_CONFIG.PAGE_LIMIT,
        });

  const { data, isLoading, error } = useQuery<CompanyPaginator, Error>(
    [url, organizationId],
    () => request,
    {
      enabled: savedCompanies.length && useSaved ? false : !!organizationId,
    },
  );

  return {
    data,
    isLoading,
    error,
  };
}

export function useCreateOrganization() {
  return useMutation(
    ({ payload }: { payload: CreateOrganizationDto }) =>
      API.OrganizationAPI.create(payload),
    {
      onSuccess: () => {
        toast.success("Record created successfully");
      },
      onError: API.Handlers.onErrorHandler,
    },
  );
}

export function useUpdateOrganization() {
  return useMutation(
    ({ payload, id }: { payload: UpdateOrganizationDto; id: string }) =>
      API.OrganizationAPI.update(id, payload),
    {
      onSuccess: () => {
        toast.success("Organization details updated successfully");
      },
      onError: API.Handlers.onErrorHandler,
    },
  );
}

export function useGetOrganizationCount(options?: CountOrganizationDto) {
  const url = ENDPOINTS.ORGANIZATION.GET_COUNT;
  const request = API.OrganizationAPI.getOrganizationCount(options);

  const { data, isLoading, error } = useQuery<
    { data: { count: number } },
    Error
  >([url, options], () => request);

  return {
    data,
    isLoading,
    error,
  };
}

export function useUpdateBillingTypeOfOrganization() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload, id }: { payload: UpdateOrgBillingTypeDto; id: string }) =>
      API.OrganizationAPI.updateBillingTypeId(id, payload),
    {
      onSuccess: () => {},
      onError: API.Handlers.onErrorHandler,
    },
  );
}

// Replaces useUpdateSubscriptionTierOfOrganization. Uses the existing
// BillingAndSubscriptionAPI.changeOrganizationSubscription method, which
// posts to /api/v1/subscriptions/change instead of the old per-organization
// PUT under OrganizationAPI.
export function useChangeOrganizationSubscription() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ payload }: { payload: ChangeOrganizationSubscriptionDto }) =>
      API.BillingAndSubscriptionAPI.changeOrganizationSubscription(payload),
    {
      onSuccess: () => {},
      onError: API.Handlers.onErrorHandler,
    },
  );
}

export function useUpdateOwnOrganization() {
  return useMutation(
    ({ payload }: { payload: UpdateOrganizationDto }) =>
      API.OrganizationAPI.updateSelf(payload),
    {
      onSuccess: () => {
        toast.success("Organization details updated successfully");
      },
      onError: API.Handlers.onErrorHandler,
    },
  );
}

export function useGetOrganizationById(id: number) {
  const url = ENDPOINTS.ORGANIZATION.DETAIL;
  const request = API.OrganizationAPI.getOrganizationById(id);

  const { data, isLoading, error } = useQuery<{ data: OrganizationDto }, Error>(
    [url, id],
    () => request,
    {
      enabled: !!id,
    },
  );

  return {
    data: data,
    isLoading,
    error,
  };
}
